import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ExternalLink, AlertCircle, RefreshCw, Activity, CheckCircle2, XCircle, HelpCircle } from 'lucide-react'
import XeroConnectionStatus from './XeroConnectionStatus'
import XeroDisconnectButton from './XeroDisconnectButton'
import XeroWebhookEventViewer from './XeroWebhookEventViewer'
import { supabase } from '../../supabase-client'

/**
 * XeroIntegrationSettings Component
 *
 * Main settings panel for Xero OAuth integration.
 * Handles authorization flow, displays connection status, and manages disconnection.
 *
 * Uses Supabase Edge Function at /functions/v1/xero-oauth for all API calls.
 *
 * @param {Object} props
 * @param {Object} props.user - Current authenticated user from Supabase
 */
export default function XeroIntegrationSettings({ user }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [connectionStatus, setConnectionStatus] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [webhookStats, setWebhookStats] = useState(null)
  const [showEventViewer, setShowEventViewer] = useState(false)

  // Load connection status on mount and when returning from OAuth
  useEffect(() => {
    fetchConnectionStatus()
    fetchWebhookStats()

    // Clear the xero_connected parameter after refetching
    if (searchParams.get('xero_connected') === 'true') {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('xero_connected')
      setSearchParams(newParams, { replace: true })
    }
  }, [searchParams.get('xero_connected')])

  /**
   * Fetch current connection status from Edge Function
   */
  const fetchConnectionStatus = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setError({ message: 'Authentication required. Please log in again.' })
        setIsLoading(false)
        return
      }

      const { data, error } = await supabase.functions.invoke('xero-oauth', {
        body: { action: 'status' },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to fetch connection status')
      }

      // Transform backend response to match XeroConnectionStatus component expectations
      if (data && data.connection) {
        setConnectionStatus({
          connected: data.connection.isActive,
          organisation: {
            name: data.connection.tenantName,
            type: data.connection.tenantType
          },
          tokenExpiresAt: data.token?.expiresAt,
          lastSyncAt: data.lastSyncAt
        })
      } else {
        setConnectionStatus(data)
      }
    } catch (err) {
      console.error('[XeroIntegration] Failed to fetch status:', err)
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Initiate OAuth authorization flow
   * Redirects to Xero login page
   */
  const handleConnect = async () => {
    setIsConnecting(true)
    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setError({ message: 'Authentication required. Please log in again.' })
        setIsConnecting(false)
        return
      }

      const { data, error } = await supabase.functions.invoke('xero-oauth', {
        body: { action: 'authorize' },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to initiate authorization')
      }

      // Redirect to Xero authorization URL
      if (data.authorizationUrl) {
        window.location.href = data.authorizationUrl
      } else {
        throw new Error('No authorization URL returned')
      }
    } catch (err) {
      console.error('[XeroIntegration] Failed to connect:', err)
      setError(err)
      setIsConnecting(false)
    }
  }

  /**
   * Disconnect from Xero
   * Revokes tokens and clears database config
   */
  const handleDisconnect = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        throw new Error('Authentication required. Please log in again.')
      }

      const { error } = await supabase.functions.invoke('xero-oauth', {
        body: { action: 'disconnect' },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to disconnect')
      }

      // Refresh status after successful disconnect
      await fetchConnectionStatus()
    } catch (err) {
      console.error('[XeroIntegration] Failed to disconnect:', err)
      throw err // Re-throw for XeroDisconnectButton to handle
    }
  }

  /**
   * Fetch webhook statistics from Edge Function
   * Uses service_role access to bypass RLS restrictions
   */
  const fetchWebhookStats = async () => {
    try {
      // Get session for auth header
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        console.error('[XeroIntegration] Not authenticated')
        return
      }

      // Call Edge Function instead of direct queries
      const { data, error } = await supabase.functions.invoke('xero-webhook-stats', {
        body: { action: 'get_stats' },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      setWebhookStats(data)
    } catch (err) {
      console.error('[XeroIntegration] Failed to fetch webhook stats:', err)
    }
  }

  const isConnected = connectionStatus?.connected || false

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Xero Integration</CardTitle>
            <CardDescription>
              Connect your Xero account to automatically sync invoices and financial data
            </CardDescription>
          </div>
          {isConnected && (
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchConnectionStatus}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error.message || 'An error occurred'}
              {'. '}
              Please try again or contact support at{' '}
              <a href="mailto:support@caledoniamushrooms.com" className="underline">
                support@caledoniamushrooms.com
              </a>
            </AlertDescription>
          </Alert>
        )}

        {/* Connection Status */}
        <div>
          <h3 className="text-sm font-medium mb-3">Connection Status</h3>
          <XeroConnectionStatus
            connectionStatus={connectionStatus}
            isLoading={isLoading}
            error={error}
            onReconnect={handleConnect}
          />
        </div>

        <Separator />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {!isConnected ? (
            <Button
              onClick={handleConnect}
              disabled={isConnecting || isLoading}
              className="flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              {isConnecting ? 'Connecting...' : 'Connect to Xero'}
            </Button>
          ) : (
            <XeroDisconnectButton
              onDisconnect={handleDisconnect}
              disabled={isLoading}
            />
          )}
        </div>

        {/* Webhook Status Section (only shown when connected) */}
        {isConnected && (
          <>
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Webhook Status</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchWebhookStats}
                  className="hover:bg-gray-50 transition-colors duration-200"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {/* Webhook Endpoint URL */}
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Webhook Endpoint</div>
                  <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200 break-all">
                    {window.location.origin}/functions/v1/xero-webhook
                  </code>
                </div>

                {/* Recent Activity Stats */}
                {webhookStats && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded p-3 border border-gray-200">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-gray-500" />
                        <div className="text-xs text-gray-600">Total (24h)</div>
                      </div>
                      <div className="text-lg font-semibold text-gray-900 mt-1">
                        {webhookStats.total}
                      </div>
                    </div>

                    <div className="bg-white rounded p-3 border border-gray-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <div className="text-xs text-gray-600">Success</div>
                      </div>
                      <div className="text-lg font-semibold text-green-700 mt-1">
                        {webhookStats.successful}
                      </div>
                    </div>

                    <div className="bg-white rounded p-3 border border-gray-200">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <div className="text-xs text-gray-600">Failed</div>
                      </div>
                      <div className="text-lg font-semibold text-red-700 mt-1">
                        {webhookStats.failed}
                      </div>
                    </div>
                  </div>
                )}

                {/* Last Successful Webhook */}
                {webhookStats?.lastSuccessful && (
                  <div className="text-xs text-gray-600">
                    Last successful webhook:{' '}
                    <span className="font-medium text-gray-900">
                      {new Date(webhookStats.lastSuccessful).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}

                {/* View All Events Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEventViewer(!showEventViewer)}
                  className="w-full border-gray-200 hover:bg-gray-100 transition-colors duration-200"
                >
                  {showEventViewer ? 'Hide' : 'View'} Webhook Events
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Webhook Event Viewer (shown when toggled) */}
        {isConnected && showEventViewer && (
          <>
            <Separator />
            <XeroWebhookEventViewer user={user} />
          </>
        )}

        {/* Learn More Dialog */}
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="self-start border-gray-300">
              <HelpCircle className="w-4 h-4 mr-2" />
              Learn More About Xero Integration
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>What happens when you connect?</DialogTitle>
              <DialogDescription>
                Understanding the Xero integration and your data security
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p>Odin will securely access your Xero organisation data</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p>Invoices created in Odin will automatically sync to Xero</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p>You can revoke access at any time from this page</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p>Your Xero credentials are never stored in Odin</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Testing Information (only in development) */}
        {import.meta.env.DEV && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Development Mode:</strong> Connected to Xero demo organisation
              "Glasgow Mushroom Company"
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
